'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useOptionalCredentialsContext } from '../../../credentials';
import { TraceLine } from '../../trace';
import { extractLoginCredentialId } from '../../extractors/login-extractors';
import type { CardRenderContext } from '../../tool-config-types';

const usernameFromOutput = (output: unknown): string | undefined => {
  if (typeof output !== 'string') {
    return undefined;
  }

  const match = /^Authenticated as ([^\n(]+)/.exec(output);
  const username = match?.[1]?.trim();

  return username && username.length > 0 ? username : undefined;
};

function AuthenticateChatRow({ ctx }: { ctx: CardRenderContext }) {
  const t = useTranslations('designSystem.chat.authenticate');
  const { toolCallData, labelPresent } = ctx;
  const credentials = useOptionalCredentialsContext()?.credentialsPreview ?? [];
  const credentialId = extractLoginCredentialId(toolCallData.input);
  const previewUsername = credentials.find(
    (credential) => credential.id === credentialId,
  )?.username;
  const username =
    (typeof previewUsername === 'string' ? previewUsername.trim() : '') ||
    usernameFromOutput(toolCallData.output);
  const isRunning =
    toolCallData.status === 'started' || toolCallData.status === 'in-progress';
  const isError =
    toolCallData.status === 'error' || Boolean(toolCallData.error);
  const label = isRunning
    ? labelPresent
    : isError
      ? toolCallData.error || t('authenticationFailed')
      : username
        ? t('authenticatedAs', { username })
        : t('success');

  return <TraceLine title={label} status={isRunning ? 'running' : undefined} />;
}

export const renderAuthenticateChatRow = (
  ctx: CardRenderContext,
): ReactNode => <AuthenticateChatRow ctx={ctx} />;
